/**
 * Sticky add to cart (sections/sticky-atc.liquid).
 *
 * Two jobs: show the card once the real Add to cart has scrolled ABOVE the
 * viewport, and keep it in step with the variant the visitor has chosen.
 *
 * "Above", not merely "not visible". An IntersectionObserver fires when the
 * target leaves the viewport in EITHER direction, so reacting to that alone
 * would show the card at the top of the page - before the visitor has even
 * reached the button it is standing in for. The bounding rect's top tells us
 * which way it went.
 *
 * The variant mirroring is not a nicety. A sticky bar that adds the product's
 * default variant while the visitor has selected another is not a cosmetic
 * bug, it is a wrong order - so the hidden input, the price, the label and the
 * disabled state all follow `variant:changed` from assets/variant-picker.js.
 */
(function () {
  function setup(card) {
    /* The main buy buttons are rendered by snippets/buy-buttons.liquid. If
       they are absent - a merchant removed the block - there is nothing for
       this card to stand in for, so it stays hidden rather than floating over
       a page that never offered the product. */
    var anchor = document.querySelector('.buy-buttons');
    if (!anchor) return;

    var variantInput = card.querySelector('[data-sticky-atc-variant-id]');
    var variantLabel = card.querySelector('[data-sticky-atc-variant]');
    var priceEl = card.querySelector('[data-sticky-atc-price]');
    var submit = card.querySelector('[data-sticky-atc-submit]');
    var label = card.querySelector('[data-sticky-atc-label]');

    /* Pre-order products: the Essential Pre-order app hides every Add to cart
       button inside a product form (inline position: fixed; left: -9999px),
       this card's included, but only puts its own Pre-order button in the main
       buy box - so the card was left with nothing to press. Its button must
       go through the app (it attaches the pre-order campaign to the line), so
       the card shows a stand-in that presses the app's button. */
    var proxy = null;
    if (submit) {
      proxy = document.createElement('button');
      proxy.type = 'button';
      proxy.className = submit.className;
      proxy.hidden = true;
      proxy.setAttribute('data-sticky-atc-proxy', '');
      submit.parentNode.parentNode.insertBefore(proxy, submit.parentNode.nextSibling);
      proxy.addEventListener('click', function () {
        var appButton = findAppButton();
        if (appButton) appButton.click();
      });
    }

    function findAppButton() {
      var buttons = anchor.querySelectorAll('[data-essential-preorder-element="button"]');
      for (var i = 0; i < buttons.length; i++) {
        if (buttons[i].getClientRects().length && !buttons[i].closest('[data-sticky-atc]')) return buttons[i];
      }
      return null;
    }

    function syncProxy() {
      if (!proxy) return;
      var appButton = submit.style.left === '-9999px' ? findAppButton() : null;
      proxy.hidden = !appButton;
      if (!appButton) return;
      proxy.textContent = appButton.textContent.trim();
      proxy.disabled = appButton.disabled;
    }

    if (proxy && 'MutationObserver' in window) {
      var queued = false;
      var queueSync = function () {
        if (queued) return;
        queued = true;
        window.setTimeout(function () {
          queued = false;
          syncProxy();
        }, 50);
      };
      var watcher = new MutationObserver(queueSync);
      watcher.observe(submit, { attributes: true, attributeFilter: ['style'] });
      watcher.observe(anchor, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'style', 'class'] });
    }
    syncProxy();

    function show() {
      if (!card.hidden) return;
      card.hidden = false;
      syncProxy();
      /* Publish the card's height so the chat bubble can sit above it on
         phones (section-mobile-dock.css). */
      document.documentElement.style.setProperty('--sticky-atc-offset', card.offsetHeight + 'px');
      /* Reveal on the next frame. Removing [hidden] and adding the class in
         the same frame gives the browser no starting state to animate from,
         so the card would appear instantly instead of sliding in. */
      window.requestAnimationFrame(function () {
        card.classList.add('is-visible');
      });
    }

    function hide() {
      if (card.hidden) return;
      card.classList.remove('is-visible');
      card.hidden = true;
      document.documentElement.style.setProperty('--sticky-atc-offset', '0px');
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          var entry = entries[0];
          var passedAbove = entry.boundingClientRect.top < 0;
          if (!entry.isIntersecting && passedAbove) show();
          else hide();
        },
        { threshold: 0 }
      ).observe(anchor);
    }

    document.addEventListener('variant:changed', function (event) {
      var variant = event.detail && event.detail.variant;

      if (!variant) {
        /* No variant matches the current selection - an unavailable
           combination. Disabling is the honest response; leaving the previous
           variant in the input would add something the visitor did not
           choose. */
        if (submit) submit.disabled = true;
        return;
      }

      if (variantInput) variantInput.value = variant.id;
      if (variantLabel) variantLabel.textContent = variant.title;

      if (priceEl && !priceEl.querySelector('.price--tba')) {
        var onSale =
          Boolean(variant.compare_at_price_amount) &&
          variant.compare_at_price_amount > variant.price_amount;
        var html = '<div class="price' + (onSale ? ' price--on-sale' : '') + '">';
        html += '<span class="price__current">' + variant.price + '</span>';
        if (onSale) {
          html += '<s class="price__compare">' + variant.compare_at_price + '</s>';
        }
        html += '</div>';
        priceEl.innerHTML = html;
      }

      if (submit) submit.disabled = !variant.available;
      if (label) {
        label.textContent = variant.available
          ? label.dataset.addLabel || label.textContent
          : label.dataset.soldLabel || label.textContent;
      }
    });
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-sticky-atc]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
